import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Label,
  Progress,
  Row,
  Spinner,
  Table,
  UncontrolledDropdown,
} from "reactstrap";

// react table
import {
  Table as ReactTable,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

// redux toolkit
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  fetchAgreementInvoice,
  fetchAgreements,
} from "../../slices/apps/agreementSlice";

// hooks
import { useProfile } from "../../Components/Hooks/UserHooks";

// react select
import Select from "react-select";

// router
import { Link } from "react-router-dom";

// diğer importlar
import moment from "moment/moment";

//Import Flatepicker
import Flatpickr from "react-flatpickr";
import { Turkish } from "flatpickr/dist/l10n/tr.js";

// sayfalar, modallar vb.
// import AgreementUpdateModal from "./AgreementUpdateModal";
// import AgreementViewModal from "./AgreementViewModal";
// import AgreementPayModal from "./AgreementPayModal";
// import AgreementSmsModal from "./AgreementSmsModal";
// import AgreementInvoiceModal from "./AgreementInvoiceModal";
// import AgreementViewInvoiceModal from "./AgreementViewInvoiceModal";
// import AgreementPassiveModal from "./AgreementPassiveModal";
// import AgreementPayApproveModal from "./AgreementPayApproveModal";
import AgreementViewModal from "../AgreementNew/AgreementViewModal";
import AgreementUpdateModal from "../AgreementNew/AgreementUpdateModal";
import AgreementPayModal from "../AgreementNew/AgreementPayModal";
import AgreementSmsModal from "../AgreementNew/AgreementSmsModal";
import AgreementInvoiceModal from "../AgreementNew/AgreementInvoiceModal";
import AgreementPassiveModal from "../AgreementNew/AgreementPassiveModal";
import AgreementPayApproveModal from "../AgreementNew/AgreementPayApproveModal";
import AgreementPayRefuseModal from "../AgreementNew/AgreementPayRefuseModal";
import AgreementReamingModal from "../AgreementNew/AgreementReamingModal";
import ReceiptViewModal from "../AgreementNew/ReceiptViewModal";

// değişkenler
const paymentTypes = { 0: "KART", 1: "EFT", 2: "HEDİYE", 3: "KOMİSYON" };

// Column Definitions
const columnHelper = createColumnHelper();

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      id="search-bar-0"
      className="form-control border-0 search"
      autoComplete="off"
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      }}
    />
  );
};

const AgreementTable = () => {
  const dispatch = useDispatch();
  const { userProfile } = useProfile();
  const [dateRange, setDateRange] = useState(null);

  const { agreements, agreementsLoading } = useSelector((state) => {
    return {
      agreements: state.agreement.agreements,
      agreementsLoading: state.agreement.agreementsLoading,
    };
  }, shallowEqual);

  // States
  const [data, setData] = useState([]); // table için data dosyası
  const [globalFilter, setGlobalFilter] = useState(""); // arama çubuğu

  const [filterAgency, setFilterAgency] = useState(""); // Acente filtresi

  const [selectedRowData, setSelectedRowData] = useState(null);
  const [modalState, setModalState] = useState({
    view: false,
    update: false,
    pay: false,
    sms: false,
    invoice: false,
    viewInvoice: false,
    viewReceipt: false,
    passive: false,
    payApprove: false,
    payRefuse: false,
    reaming: false,
  });

  const toggleModal = (modalName, rowData = null) => {
    setSelectedRowData(rowData);
    setModalState((prevState) => ({
      ...prevState,
      [modalName]: !prevState[modalName],
    }));
  };

  const handleViewInvoice = async (row) => {
    try {
      await dispatch(fetchAgreementInvoice({ id: row.Id, type: "pdf" }));
    } catch (error) {
      console.error("Fatura görüntülenemedi:", error);
    }
  };

  // filtreleme işlemi için
  useEffect(() => {
    setData([]);

    const filteredData = agreements?.filter((data) => {
      if (
        filterAgency.toString() &&
        data.AgencyId.toString() !== filterAgency.toString()
      )
        return false;

      if (
        globalFilter &&
        !Object.values(data).some(
          (value) =>
            typeof value === "string" &&
            (value
              .toLocaleLowerCase()
              .includes(globalFilter.toLocaleLowerCase()) ||
              value
                .toLocaleUpperCase()
                .includes(globalFilter.toLocaleUpperCase()))
        )
      )
        return false;

      if (dateRange) {
        const created = new Date(data.IssuedTime);
        const start = new Date(dateRange.start);
        const end = new Date(dateRange.end);

        if (created < start || created > end) return false;
      }

      return true;
    });

    setData(filteredData);
  }, [globalFilter, filterAgency, agreements, dateRange]);

  // Hooks
  const columns = useMemo(
    () => [
      columnHelper.accessor("actions1", {
        header: "",
        size: 46,
        minSize: 46,
        maxSize: 46,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                tag="button"
                className="btn btn-sm btn-ghost-secondary btn-icon"
              >
                <i className="ri-more-2-fill fs-16"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end" strategy="fixed">
                {userProfile.data.Role == "SuperAdmin" &&
                  !row.original.IsPaid &&
                  row.original.PaymentType == 1 && (
                    <DropdownItem
                      onClick={() => toggleModal("payApprove", row.original)}
                    >
                      <i className="ri-thumb-up-line align-middle me-1"></i>{" "}
                      Ödeme Onayla
                    </DropdownItem>
                  )}

                {!row.original.IsPaid ? (
                  <DropdownItem
                    onClick={() => toggleModal("pay", row.original)}
                  >
                    <i className="ri-secure-payment-line align-middle me-1"></i>{" "}
                    Ödeme Yap
                  </DropdownItem>
                ) : (
                  <DropdownItem
                    onClick={() => toggleModal("view", row.original)}
                  >
                    <i className="ri-eye-line align-middle me-1"></i> Görüntüle
                  </DropdownItem>
                )}

                <DropdownItem
                  onClick={() => toggleModal("reaming", row.original)}
                  disabled={!row.original.IsActive || !row.original.IsPaid}
                >
                  <i className="ri-list-check-2 align-middle me-1"></i> Kalan
                  Haklar
                </DropdownItem>

                <DropdownItem
                  onClick={() => handleViewInvoice(row.original)}
                  disabled={row.original.InvoiceUUID == null}
                >
                  <i className="ri-find-replace-line align-middle me-1"></i>{" "}
                  Fatura Görüntüle
                </DropdownItem>

                <DropdownItem
                  onClick={() => toggleModal("viewReceipt", row.original)}
                  disabled={
                    !row.original.IsPaid || row.original.PaymentType == 2
                  }
                >
                  <i className="ri-phone-find-line align-middle me-1"></i>{" "}
                  Tahsilat Makbuzu Görüntüle
                </DropdownItem>

                <DropdownItem
                  onClick={() => toggleModal("update", row.original)}
                  disabled={userProfile.data.Role != "SuperAdmin"}
                >
                  <i className="ri-pencil-line align-middle me-1"></i> Güncelle
                </DropdownItem>

                {row.original.PaymentType != 2 &&
                userProfile.data.Role != "SuperAdmin"
                  ? null
                  : row.original.IsPaid && (
                      <DropdownItem
                        onClick={() => toggleModal("passive", row.original)}
                      >
                        {row.original.IsActive ? (
                          <>
                            <i className="ri-close-circle-line align-middle me-1"></i>{" "}
                            Pasif Yap
                          </>
                        ) : (
                          <>
                            <i className="ri-checkbox-circle-line align-middle me-1"></i>{" "}
                            Aktif Yap
                          </>
                        )}
                      </DropdownItem>
                    )}

                {userProfile.data.Role == "SuperAdmin" &&
                  row.original.IsPaid && (
                    <DropdownItem
                      onClick={() => toggleModal("payRefuse", row.original)}
                    >
                      <i className="ri-close-circle-line align-middle me-1"></i>{" "}
                      Ödenmedi Yap
                    </DropdownItem>
                  )}

                {userProfile.data.Role == "SuperAdmin" && (
                  <DropdownItem
                    onClick={() => toggleModal("invoice", row.original)}
                    disabled={
                      row.original.InvoiceUUID != null ||
                      !row.original.IsActive ||
                      !row.original.IsPaid ||
                      row.original.PaymentType == 2
                    }
                  >
                    <i className="ri-bill-line align-middle me-1"></i>{" "}
                    Faturalandır
                  </DropdownItem>
                )}

                <DropdownItem onClick={() => toggleModal("sms", row.original)}>
                  <i className="ri-message-2-line align-middle me-1"></i> SMS
                  Gönder
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      }),
      // columnHelper.accessor("actions", {
      //   header: "",
      //   size: 120,
      //   minSize: 120,
      //   maxSize: 120,
      //   enableSorting: false,
      //   cell: ({ row }) => {
      //     return (
      //       <div className="d-flex flex-wrap" style={{ gap: "5px" }}>
      //         <AgreementSmsModal id={row.original.Id} />
      //         {!row.original.IsPaid ? (
      //           <AgreementPayModal id={row.original.Id} />
      //         ) : (
      //           <AgreementViewModal id={row.original.Id} />
      //         )}
      //         <AgreementUpdateModal id={row.original.Id} />

      //         {row.original.PaymentType == 2 &&
      //         userProfile.data.Role != "SuperAdmin"
      //           ? null
      //           : row.original.IsPaid && (
      //               <AgreementPassiveModal
      //                 id={row.original.Id}
      //                 isActive={row.original.IsActive}
      //               />
      //             )}

      //         <AgreementInvoiceModal
      //           id={row.original.Id}
      //           isDisabled={
      //             row.original.InvoiceUUID != null ||
      //             !row.original.IsActive ||
      //             !row.original.IsPaid ||
      //             row.original.PaymentType == 2
      //               ? true
      //               : false
      //           }
      //         />
      //         <AgreementViewInvoiceModal id={row.original.Id} />
      //         {userProfile.data.Role == "SuperAdmin" &&
      //           !row.original.IsPaid &&
      //           row.original.PaymentType == 1 && (
      //             <AgreementPayApproveModal id={row.original.Id} />
      //           )}
      //       </div>
      //     );
      //   },
      // }),
      columnHelper.accessor("PaymentType", {
        header: "",
        size: 120,
        minSize: 120,
        maxSize: 120,
        cell: ({ row }) => {
          return (
            <div>
              <div className="text-truncate  fw-bold fs-12 ">
                <i className="bx bxs-circle align-middle me-1 fs-14 text-dark"></i>
                {paymentTypes[row.original.PaymentType]}
              </div>

              {row.original.IsPaid ? (
                <div className="text-truncate  fw-bold fs-12 ">
                  <i className="bx bxs-check-circle align-middle me-1 fs-14 text-info"></i>
                  Ödendi
                </div>
              ) : (
                <div className="text-truncate fw-bold fs-12 ">
                  <i className="bx bxs-info-circle align-middle me-1 fs-14 text-danger"></i>
                  Ödenmedi
                </div>
              )}
              {row.original.IsActive ? (
                <div className="text-truncate  fw-bold fs-12 ">
                  <i className="bx bxs-circle align-middle me-1 fs-14 text-success"></i>
                  Aktif
                </div>
              ) : (
                <div className="text-truncate fw-bold fs-12 ">
                  <i className="bx bxs-circle align-middle me-1 fs-14 text-warning"></i>
                  Pasif
                </div>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor("AggreementName", {
        header: "Sözleşme Bilgileri",
        size: "auto",
        minSize: 250,
        maxSize: 350,
        enableResizing: false,
        cell: ({ row }) => {
          return (
            <div className="text-truncate fs-16 fw-semibold">
              {row.original.AggreementName}
              <div className="text-truncate fw-medium fs-12">
                {row.original.AggrementNo}
              </div>
              <div className="text-truncate fw-medium fs-12">
                {moment(row.original.CreateDatetime).format("DD.MM.YYYY")}
              </div>
            </div>
          );
        },
      }),

      columnHelper.accessor("Earning", {
        header: "Hak Ediş",
        size: 120,
        minSize: 120,
        maxSize: 120,
        cell: ({ row }) => {
          return (
            <div className="text-truncate fw-medium fs-12">
              <b className="text-dark">
                {new Intl.NumberFormat("tr-TR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(
                  userProfile.data.Role == "SubAgency"
                    ? row.original.SubEarning || 0
                    : row.original.Earning
                )}{" "}
                TL
              </b>
            </div>
          );
        },
      }),

      columnHelper.accessor("Price", {
        header: "Fiyat",
        size: 120,
        minSize: 120,
        maxSize: 120,
        cell: ({ row }) => {
          return (
            <div className="text-truncate fw-medium fs-12">
              <b className="text-dark">
                {new Intl.NumberFormat("tr-TR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(row.original.Price)}{" "}
                TL
              </b>
            </div>
          );
        },
      }),

      columnHelper.accessor("AgencyName", {
        header: "Acente",
        size: 150,
        minSize: 150,
        maxSize: 150,
        cell: ({ row }) => {
          return (
            <div>
              <div className="text-truncate fw-semibold fs-12">
                {row.original.AgencyName ? row.original.AgencyName : "-"}
              </div>
              <div className="text-truncate fw-semibold fs-12">
                {row.original.District ? row.original.District : "-"}/
                {row.original.City ? row.original.City : "-"}
              </div>
            </div>
          );
        },
      }),

      columnHelper.accessor("CustomerName", {
        header: "Müşteri Bilgiler",
        size: 200,
        minSize: 200,
        maxSize: 200,
        cell: ({ row }) => {
          return (
            <div className="text-truncate fw-medium fs-12">
              <div className="text-truncate">
                <i
                  className="bx bxs-user align-middle text-dark me-1"
                  id="tooltipTopActive"
                ></i>
                <b className="text-dark">{row.original.CustomerName}</b>
              </div>
              <div className="text-truncate">
                <i className="mdi mdi-phone align-middle text-dark me-1"></i>
                <b className="text-dark">
                  +90{" "}
                  {row.original.PhoneNumber?.toString().replace(
                    /(\d{3})(\d{3})(\d{2})(\d{2})/,
                    "$1 $2 $3 $4"
                  )}
                </b>
              </div>
            </div>
          );
        },
      }),

      columnHelper.accessor("CarCategory", {
        header: "Araç Bilgiler",
        size: 250,
        minSize: 250,
        maxSize: 250,
        cell: ({ row }) => {
          return (
            <div className="text-truncate fw-semibold">
              <div className="text-truncate fw-medium fs-12">
                <i
                  className="bx bxs-category align-middle text-dark me-1"
                  id="tooltipTopActive"
                ></i>
                <b className="text-dark">
                  {row.original.CarCategory ? row.original.CarCategory : "-"}
                </b>
              </div>
              <div className="text-truncate fw-medium fs-12">
                <i
                  className="bx bxs-car align-middle text-dark me-1"
                  id="tooltipTopActive"
                ></i>
                <b className="text-dark">
                  {row.original.CarBrand} - {row.original.CarModel}
                </b>
              </div>
              <div className="text-truncate fw-medium fs-12">
                <i
                  className="bx bxs-right-arrow align-middle text-dark me-1"
                  id="tooltipTopActive"
                ></i>
                <b className="text-dark">{row.original.CarPlate}</b>
              </div>
              <div className="text-truncate fw-medium fs-12">
                <i
                  className="mdi mdi-calendar-month align-middle text-dark me-1"
                  id="tooltipTopActive"
                ></i>
                <b className="text-dark">{row.original.CarYear}</b>
              </div>
            </div>
          );
        },
      }),

      columnHelper.accessor("StartDate", {
        header: "Tarih",
        size: 180,
        minSize: 180,
        maxSize: 180,
        cell: ({ row }) => {
          const startDate = moment(row.original.StartDate);
          const endDate = moment(row.original.EndDate);
          const today = moment();

          const hasStarted = today.isSameOrAfter(startDate);

          if (!hasStarted) {
            const daysUntilStart = startDate.diff(today, "days"); // Başlamasına kalan gün sayısı

            return (
              <div className="text-truncate fw-semibold">
                <div className="d-flex fw-medium fs-12 text-muted">
                  <i
                    className="ri-calendar-2-fill align-middle text-dark me-1"
                    id="tooltipTopActive"
                  ></i>
                  <div style={{ width: "65px" }}>S. Tarihi:</div>
                  <b className="text-dark">
                    {moment(row.original.IssuedTime).format("DD.MM.YYYY")}
                  </b>
                </div>
                <div className="d-flex fw-medium fs-12 text-muted">
                  <i
                    className="ri-calendar-2-fill align-middle text-success me-1"
                    id="tooltipTopActive"
                  ></i>
                  <div style={{ width: "65px" }}>Başlangıç:</div>
                  <b className="text-dark">
                    {moment(row.original.StartDate).format("DD.MM.YYYY")}
                  </b>
                </div>
                <div className="d-flex fw-medium fs-12 text-muted">
                  <i
                    className="ri-calendar-2-fill align-middle text-danger me-1"
                    id="tooltipTopActive"
                  ></i>
                  <div style={{ width: "65px" }}>Bitiş:</div>
                  <b className="text-dark">
                    {moment(row.original.EndDate).format("DD.MM.YYYY")}
                  </b>
                </div>
                <div className="d-flex fw-medium fs-12 text-muted">
                  <i
                    className="ri-error-warning-fill align-middle text-warning me-1"
                    id="tooltipTopActive"
                  ></i>
                  <b className="text-warning">Henüz başlamadı</b>
                </div>
              </div>
            );
          }

          const totalDays = endDate.diff(startDate, "days");
          const remainingDays = endDate.diff(today, "days");
          const percentage = (remainingDays / totalDays) * 100;

          const getColor = () => {
            if (percentage <= 30) return "danger";
            if (percentage <= 70) return "warning";
            return "success";
          };

          return (
            <div className="text-truncate fw-semibold">
              <div className="d-flex fw-medium fs-12 text-muted">
                <i
                  className="ri-calendar-2-fill align-middle text-dark me-1"
                  id="tooltipTopActive"
                ></i>
                <div style={{ width: "65px" }}>S. Tarihi:</div>
                <b className="text-dark">
                  {moment(row.original.IssuedTime).format("DD.MM.YYYY")}
                </b>
              </div>
              <div className="d-flex fw-medium fs-12 text-muted">
                <i
                  className="ri-calendar-2-fill align-middle text-success me-1"
                  id="tooltipTopActive"
                ></i>
                <div style={{ width: "65px" }}>Başlangıç:</div>
                <b className="text-dark">
                  {moment(row.original.StartDate).format("DD.MM.YYYY")}
                </b>
              </div>
              <div className="d-flex fw-medium fs-12 text-muted">
                <i
                  className="ri-calendar-2-fill align-middle text-danger me-1"
                  id="tooltipTopActive"
                ></i>
                <div style={{ width: "65px" }}>Bitiş:</div>
                <b className="text-dark">
                  {moment(row.original.EndDate).format("DD.MM.YYYY")}
                </b>
              </div>
              {row.original.IsActive && row.original.IsPaid ? (
                <>
                  <div className="d-flex fw-medium fs-12 text-muted">
                    <i
                      className="ri-indeterminate-circle-fill align-middle text-info me-1"
                      id="tooltipTopActive"
                    ></i>
                    <div style={{ width: "65px" }}>Kalan:</div>
                    <b className="text-dark">{remainingDays} Gün</b>
                  </div>
                  <Progress
                    color={getColor()}
                    value={percentage}
                    striped
                    animated
                    className="mt-2"
                    style={{ height: "8px" }}
                  />
                </>
              ) : (
                <div className="d-flex fw-medium fs-12 text-danger mt-1">
                  <i className="ri-error-warning-fill align-middle me-1"></i>
                  <b>Sözleşme aktif değil</b>
                </div>
              )}
            </div>
          );
        },
      }),
    ],
    []
  );

  // Hooks
  const table = useReactTable({
    data: data,
    columns,
    state: {},
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  //** convert to excel */

  const ExcelJS = require("exceljs");

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sözleşme Listesi");

    // Sütun başlıklarını belirtin
    worksheet.columns = [
      { header: "Durum", key: "IsActive", width: 10 },
      { header: "Ödeme Durumu", key: "IsPaid", width: 20 },
      { header: "Ödeme Yöntemi", key: "PaymentType", width: 20 },
      { header: "Sözleşme Numarası", key: "AggrementNo", width: 20 },
      { header: "Sözleşme", key: "AggreementName", width: 30 },
      { header: "TCKN/VKN", key: "TaxNumber", width: 30 },
      { header: "Sözleşme Oluşturma Tarihi", key: "IssuedTime", width: 20 },
      { header: "Sözleşme Tarihi", key: "CreateDatetime", width: 20 },
      { header: "Sözleşme Başlangıç Tarihi", key: "StartDate", width: 20 },
      { header: "Sözleşme Bitiş Tarihi", key: "EndDate", width: 20 },
      { header: "Fiyat", key: "Price", width: 20 },
      { header: "Hak Ediş", key: "Earning", width: 20 },
      { header: "Acente", key: "AgencyName", width: 80 },
      { header: "Müşteri Adı Soyadı", key: "CustomerName", width: 80 },
      { header: "Müşteri Telefon", key: "PhoneNumber", width: 30 },
      { header: "Araç Plaka", key: "CarPlate", width: 20 },
      { header: "Araç Tipi", key: "CarCategory", width: 20 },
      { header: "Araç Marka", key: "CarBrand", width: 20 },
      { header: "Araç Model", key: "CarModel", width: 20 },
      { header: "Araç Yılı", key: "CarYear", width: 20 },
    ];

    // Verileri ekleyin
    data.forEach((row, index) => {
      worksheet.addRow({
        IsActive: row.IsActive ? "Aktif" : "Pasif",
        IsPaid: row.IsPaid ? "Ödendi" : "Ödenmedi",
        PaymentType: paymentTypes[row.PaymentType],
        AggrementNo: row.AggrementNo,
        AggreementName: row.AggreementName,
        TaxNumber: row.TaxNumber || "-",
        IssuedTime: moment(row.IssuedTime).format("DD.MM.YYYY"),
        CreateDatetime: moment(row.CreateDatetime).format("DD.MM.YYYY"),
        StartDate: moment(row.StartDate).format("DD.MM.YYYY"),
        EndDate: moment(row.EndDate).format("DD.MM.YYYY"),
        Price: Intl.NumberFormat("tr-TR").format(row.Price),
        Earning:
          userProfile.data.Role == "SubAgency"
            ? Intl.NumberFormat("tr-TR").format(row.SubEarning || 0)
            : Intl.NumberFormat("tr-TR").format(row.Earning),
        AgencyName: row.AgencyName,
        CustomerName: row.CustomerName,
        PhoneNumber: row.PhoneNumber,
        CarPlate: row.CarPlate,
        CarCategory: row.CarCategory,
        CarBrand: row.CarBrand,
        CarModel: row.CarModel,
        CarYear: row.CarYear,
      });
    });

    // Excel dosyasını oluşturun ve indirme işlemi sağlayın
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const fileName = `Sözleşmeler_${moment(new Date()).format(
      "DD_MM_YYYY"
    )}.xlsx`;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  //** convert to excel */

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState,
  } = table;

  return (
    <Fragment>
      <Card>
        <CardHeader className="align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">Sözleşme Listesi</h4>
          <div className="flex-shrink-0">
            <UncontrolledDropdown className="card-header-dropdown">
              <DropdownToggle
                tag="a"
                className="text-reset"
                role="button"
                caret
              >
                İşlemler
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem onClick={() => dispatch(fetchAgreements())}>
                  <i className="ri-refresh-line label-icon align-middle me-2"></i>
                  Yenile
                </DropdownItem>
                <DropdownItem onClick={() => exportToExcel()}>
                  <i className="ri-file-excel-2-line label-icon align-middle me-2"></i>
                  Excel İndir
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        </CardHeader>
        <CardBody>
          <Row className="mb-3">
            <CardBody className="border border-dashed border-end-0 border-start-0 border-top-0">
              <form>
                <div className="d-flex flex-wrap justify-content-between">
                  <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
                    <div style={{ width: "100px" }}>
                      <select
                        className="form-select mb-3"
                        aria-label="yufus"
                        value={getState().pagination.pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                      >
                        <option defaultValue="10">10</option>
                        <option defaultValue="25">25</option>
                        <option defaultValue="50">50</option>
                        <option defaultValue="100">100</option>
                        <option defaultValue="200">200</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <div
                      className="search-box me-2 mb-2 d-inline-block border"
                      style={{ borderRadius: "0.25em" }}
                    >
                      <DebouncedInput
                        value={globalFilter ?? ""}
                        onChange={(value) => setGlobalFilter(value)}
                        placeholder={"Ara..."}
                      />
                      <i className="bx bx-search-alt search-icon"></i>
                    </div>
                  </div>
                </div>
              </form>
              <Row>
                {userProfile.data.Role == "SuperAdmin" && (
                  <Col md={6} lg={4}>
                    <div className="mb-3">
                      <Label htmlFor="agenciesSelect" className="form-label">
                        Acente
                      </Label>
                      <Select
                        placeholder="Seçiniz"
                        id="agenciesSelect"
                        name="agencies"
                        options={Array.from(
                          new Map(
                            agreements.map((item) => [item.AgencyId, item])
                          ).values()
                        ).map((item) => ({
                          label: item.AgencyName,
                          value: item.AgencyId,
                        }))}
                        isClearable
                        noOptionsMessage={() => "Seçenek bulunamadı"}
                        onChange={(value) => {
                          setFilterAgency(value ? value.value : "");
                        }}
                      />
                    </div>
                  </Col>
                )}
                <Col md={6} lg={4}>
                  <Label className="form-label">Tarih</Label>
                  <Flatpickr
                    className={"form-control"}
                    placeholder="Tarih seçiniz"
                    options={{
                      mode: "range",
                      dateFormat: "d.m.Y",
                      locale: { ...Turkish },
                    }}
                    onChange={(selectedDates) => {
                      if (selectedDates.length === 2) {
                        setDateRange({
                          start: selectedDates[0],
                          end: selectedDates[1],
                        });
                      } else {
                        setDateRange(null);
                      }
                    }}
                  />
                </Col>
              </Row>
            </CardBody>
          </Row>

          <div
            style={{
              overflowX: "auto",
            }}
          >
            <Table className="align-middle">
              <thead>
                {getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="text-muted"
                        style={{
                          width: header.column.columnDef.size,
                          minWidth: header.column.columnDef.minSize,
                          maxWidth: header.column.columnDef.maxSize,
                          letterSpacing: "0.8px",
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: <i className="ri-arrow-down-s-line"></i>,
                              desc: <i className="ri-arrow-up-s-line"></i>,
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {agreementsLoading ? (
                  <tr>
                    <td colSpan="100%" className="text-center pt-5 pb-5">
                      <Spinner color="primary" />
                      <p className="text-dark">Yükleniyor...</p>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan="100%"
                      className="text-center pt-5 pb-5 text-muted"
                    >
                      <h1>
                        <i className="ri-error-warning-line text-muted"></i>
                      </h1>
                      <p>Kayıt bulunamadı!</p>
                    </td>
                  </tr>
                ) : (
                  getRowModel().rows.map((row) => {
                    return (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              style={{
                                width: cell.column.columnDef.size,
                                minWidth: cell.column.columnDef.minSize,
                                maxWidth: cell.column.columnDef.maxSize,
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
            <div className="col-sm">
              <div className="text-muted">
                {data.length > 0
                  ? `Toplam ${
                      table.getFilteredRowModel().rows.length
                    } kayıttan ${
                      table.getFilteredRowModel().rows.length === 0
                        ? 0
                        : table.getState().pagination.pageIndex *
                            table.getState().pagination.pageSize +
                          1
                    }
                  ile ${Math.min(
                    (table.getState().pagination.pageIndex + 1) *
                      table.getState().pagination.pageSize,
                    table.getFilteredRowModel().rows.length
                  )} arası gösteriliyor`
                  : "Kayıt Yok"}
              </div>
            </div>
            {data.length > 0 && (
              <div className="col-sm-auto">
                <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
                  <li
                    className={
                      !getCanPreviousPage() ? "page-item disabled" : "page-item"
                    }
                  >
                    <Link to="#" className="page-link" onClick={previousPage}>
                      {"<"}
                    </Link>
                  </li>
                  {getPageOptions().map((item, key) => {
                    const pageIndex = getState().pagination.pageIndex;
                    const totalPages = getPageOptions().length;
                    const firstPage = item === 0;
                    const lastPage = item === totalPages - 1;
                    const nearCurrentPage = Math.abs(pageIndex - item) <= 1;

                    // Display logic
                    if (firstPage || lastPage || nearCurrentPage) {
                      return (
                        <React.Fragment key={key}>
                          <li className="page-item">
                            <Link
                              to="#"
                              className={
                                pageIndex === item
                                  ? "page-link active"
                                  : "page-link"
                              }
                              onClick={() => setPageIndex(item)}
                            >
                              {item + 1}
                            </Link>
                          </li>
                        </React.Fragment>
                      );
                    } else if (item === 1 && pageIndex > 2) {
                      return null;
                    } else if (
                      item === totalPages - 2 &&
                      pageIndex < totalPages - 3
                    ) {
                      return null;
                    } else {
                      return null;
                    }
                  })}
                  <li
                    className={
                      !getCanNextPage() ? "page-item disabled" : "page-item"
                    }
                  >
                    <Link to="#" className="page-link" onClick={nextPage}>
                      {">"}
                    </Link>
                  </li>
                </ul>
              </div>
            )}
          </Row>
        </CardBody>
      </Card>

      {selectedRowData && (
        <>
          <AgreementViewModal
            id={selectedRowData.Id}
            isOpen={modalState.view}
            toggle={() => toggleModal("view")}
          />
          <AgreementUpdateModal
            id={selectedRowData.Id}
            isOpen={modalState.update}
            toggle={() => toggleModal("update")}
          />
          <AgreementPayModal
            id={selectedRowData.Id}
            isOpen={modalState.pay}
            toggle={() => toggleModal("pay")}
            price={selectedRowData.Price}
          />
          <AgreementSmsModal
            id={selectedRowData.Id}
            isOpen={modalState.sms}
            toggle={() => toggleModal("sms")}
          />
          <AgreementInvoiceModal
            id={selectedRowData.Id}
            isOpen={modalState.invoice}
            toggle={() => toggleModal("invoice")}
          />
          <ReceiptViewModal
            rowData={selectedRowData}
            isOpen={modalState.viewReceipt}
            toggle={() => toggleModal("viewReceipt")}
          />
          <AgreementPassiveModal
            id={selectedRowData.Id}
            isActive={selectedRowData.IsActive}
            isOpen={modalState.passive}
            toggle={() => toggleModal("passive")}
          />
          <AgreementPayApproveModal
            id={selectedRowData.Id}
            isOpen={modalState.payApprove}
            toggle={() => toggleModal("payApprove")}
          />
          <AgreementPayRefuseModal
            id={selectedRowData.Id}
            isOpen={modalState.payRefuse}
            toggle={() => toggleModal("payRefuse")}
          />
          <AgreementReamingModal
            id={selectedRowData.AggrementNo}
            rowData={selectedRowData}
            isOpen={modalState.reaming}
            toggle={() => toggleModal("reaming")}
          />
        </>
      )}
    </Fragment>
  );
};

export default AgreementTable;
